import { dedent } from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'
import { tempTableName } from '../utils'

class GetisOrdSpacetime extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.getisordspacetime',
        group: 'Statistics',
        title: 'Getis Ord Spacetime',
        description: `
        This table function computes the space temporal Getis-Ord Gi* statistic for each index and each datetime timestamp in the input table.
                `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'indexcol',
          type: WorkflowComponentParamType.Column,
          title: 'Index column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'datecol',
          type: WorkflowComponentParamType.Column,
          title: 'Date column',
          parent: 'source',
          dataType: [ColumnDataType.Datetime, ColumnDataType.Timestamp, ColumnDataType.Date]
        },
        {
          name: 'valuecol',
          type: WorkflowComponentParamType.Column,
          title: 'Value column',
          parent: 'source',
          dataType: [ColumnDataType.Number]
        },
        {
          name: 'kernel',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function for spatial weights',
          options: ['uniform', 'triangular', 'quadratic', 'quartic', 'gaussian']
        },
        {
          name: 'kerneltime',
          type: WorkflowComponentParamType.Selection,
          title: 'Kernel function for temporal weights',
          options: ['uniform', 'triangular', 'quadratic', 'quartic', 'gaussian']
        },
        {
          name: 'size',
          type: WorkflowComponentParamType.Number,
          title: 'Size',
          default: 3,
          min: 1,
          max: 10
        },
        {
          name: 'bandwidth',
          type: WorkflowComponentParamType.Number,
          title: 'Temporal bandwidth',
          default: 3,
          min: 1,
          max: 10
        },
        {
          name: 'timeinterval',
          type: WorkflowComponentParamType.Selection,
          title: 'Time interval',
          options: ['year', 'quarter', 'month', 'week', 'day', 'hour', 'minute', 'second']
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const source = preparedParams.source as string
    const at = connection.quoteName(envVars.analyticsToolboxDataset as string)
    let query = ''
    switch (connection.type) {
      case Provider.BigQuery: {
        let outTable = tablename
        let copyFromTempTable = ''
        if (codeType === WorkflowCodeType.StoredProcedure) {
          outTable = `${connection.tempLocation}.${tempTableName(componentName, preparedParams.source)}`
          copyFromTempTable = connection.getCreateTempTableQuery(
            tablename,
            `SELECT * FROM ${connection.quoteName(outTable)}`,
            codeType,
            null
          )
        }
        query = dedent`
          DECLARE grid_type STRING;
          DECLARE grid_resolution INT64;

          CALL ${at}.__CHECK_GRID_INDEX_COLUMN(
              'SELECT * FROM ${connection.quoteName(source)}',
              '${params.indexcol as string}',
              grid_type,
              grid_resolution
          );

          ${
            codeType === WorkflowCodeType.StoredProcedure
              ? `DROP TABLE IF EXISTS ${connection.quoteName(outTable)};`
              : ''
          }
          IF grid_type = 'quadbin' THEN
              CALL ${at}.GETIS_ORD_SPACETIME_QUADBIN_TABLE(
                  'SELECT * EXCEPT (${params.datecol}), CAST(${params.datecol} AS DATETIME) AS ${
          params.datecol
        } FROM ${connection.quoteName(source)}',
                  '${outTable}',
                  '${params.indexcol as string}',
                  '${params.datecol as string}',
                  '${params.valuecol as string}',
                  CAST(${params.size} AS INTEGER),
                  '${params.timeinterval}',
                  CAST(${params.bandwidth} AS INTEGER),
                  '${params.kernel}',
                  '${params.kerneltime}'
              );
          ELSEIF grid_type = 'h3' THEN
              CALL ${at}.GETIS_ORD_SPACETIME_H3_TABLE(
                'SELECT * EXCEPT (${params.datecol}), CAST(${params.datecol} AS DATETIME) AS ${
          params.datecol
        } FROM ${connection.quoteName(source)}',
                '${outTable}',
                '${params.indexcol as string}',
                '${params.datecol as string}',
                '${params.valuecol as string}',
                CAST(${params.size} AS INTEGER),
                '${params.timeinterval}',
                CAST(${params.bandwidth} AS INTEGER),
                '${params.kernel}',
                '${params.kerneltime}'
              );
          ELSE
              RAISE USING MESSAGE = 'Invalid spatial index column, please select a column that contains h3 or quadbin indexes.';
          END IF;
          ${copyFromTempTable}
      `
        break
      }
      case Provider.Snowflake:
        query = dedent`
              DECLARE
                grid_type STRING;
                WRONG_GRID_TYPE_EXCEPTION EXCEPTION;
              BEGIN
                grid_type := (
                  CALL ${at}._CHECK_GRID_INDEX_COLUMN(
                    'SELECT * FROM ${source}',
                    '${params.indexcol as string}'
                  )
                );
                IF (grid_type != 'unsupported') THEN
                    ${
                      codeType === WorkflowCodeType.StoredProcedure
                        ? `DROP TABLE IF EXISTS ${connection.quoteName(tablename)};`
                        : ''
                    }
                    EXECUTE IMMEDIATE '
                        CALL ${at}.GETIS_ORD_SPACETIME_' || grid_type || '(
                            ''SELECT * FROM ${connection.quoteName(source)}'',
                            ''${tablename}'',
                            ''${params.indexcol as string}'',
                            ''${params.datecol as string}'',
                            ''${params.valuecol as string}'',
                            CAST(${params.size} AS INTEGER),
                            ''${(params.timeinterval as string).toUpperCase()}'',
                            CAST(${params.bandwidth} AS INTEGER),
                            ''${params.kernel}'',
                            ''${params.kerneltime}''
                        );
                    ';
                ELSE
                  RAISE WRONG_GRID_TYPE_EXCEPTION;
                END IF;
              END;
            `
        break
    }

    query = connection.wrapCodeInIfTableExistsBlock(query, [tablename], codeType)

    return {
      outputs: { result: tablename },
      query
    }
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const indexType = columns.source.find((c) => c.name === params.indexcol)?.type
    return [
      {
        name: 'index',
        type: indexType as ColumnDataType
      },
      {
        name: 'date',
        type: ColumnDataType.Datetime
      },
      {
        name: 'gi',
        type: ColumnDataType.Number
      },
      {
        name: 'p_value',
        type: ColumnDataType.Number
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake]
  }
}

export { GetisOrdSpacetime }
